import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_API_ERROR } from "../../../constants/commonConstants";
import {
	ICorporateEvent,
	ICorporateEventBid,
	ICorporateEventBidsResponse,
} from "../../../interfaces/corporateEvent";
import { iApiError } from "../../../types/commonTypes";
import {
	declineCorporateEventThunk,
	getCorporateEventBidsThunk,
	getCorporateEventDetailThunk,
	getCorporateEventsHistoryThunk,
	getCorporateEventsThunk,
} from "./corporateEventThunk";

export interface ICorporateEventSlice {
	corporateEvents: ICorporateEvent[];
	corporateEventsLoading: boolean;
	corporateEventsApiError: iApiError | null;
	// Corporate Event Detail
	corporateEventDetail: ICorporateEvent | null;
	corporateEventDetailLoading: boolean;
	corporateEventDetailApiError: iApiError | null;
	// Corporate Event Bids
	corporateEventBids: ICorporateEventBidsResponse;
	corporateEventBidsLoading: boolean;
	corporateEventBidsApiError: iApiError | null;
}

const initialState: ICorporateEventSlice = {
	corporateEvents: [],
	corporateEventsLoading: false,
	corporateEventsApiError: null,
	// Corporate Event Detail
	corporateEventDetail: null,
	corporateEventDetailLoading: false,
	corporateEventDetailApiError: null,
	// Corporate Event Bids
	corporateEventBids: {
		currentBid: {} as ICorporateEventBid,
		history: [],
	},
	corporateEventBidsLoading: false,
	corporateEventBidsApiError: null,
};

const corporateEventSlice = createSlice({
	name: "corporateEvent",
	initialState,
	reducers: {
		resetCorporateEventsApiError(state) {
			state.corporateEventsApiError = null;
		},
		setCorporateEventDetail(state, { payload }: PayloadAction<ICorporateEvent | null>) {
			state.corporateEventDetail = payload;
		},
		resetCorporateEventDetailApiError(state) {
			state.corporateEventDetailApiError = null;
		},
	},
	// Extra Reducers
	extraReducers: (builder) => {
		builder.addCase(getCorporateEventsThunk.pending, (state) => {
			state.corporateEventsLoading = true;
		});
		builder.addCase(getCorporateEventsThunk.fulfilled, (state, action) => {
			state.corporateEvents = action.payload;
			state.corporateEventsLoading = false;
		});
		builder.addCase(getCorporateEventsThunk.rejected, (state, action) => {
			state.corporateEventsApiError = action.payload ?? DEFAULT_API_ERROR;
			state.corporateEventsLoading = false;
		});

		builder.addCase(getCorporateEventsHistoryThunk.pending, (state) => {
			state.corporateEventsLoading = true;
		});
		builder.addCase(getCorporateEventsHistoryThunk.fulfilled, (state, action) => {
			state.corporateEvents = action.payload;
			state.corporateEventsLoading = false;
		});
		builder.addCase(getCorporateEventsHistoryThunk.rejected, (state, action) => {
			state.corporateEventsApiError = action.payload ?? DEFAULT_API_ERROR;
			state.corporateEventsLoading = false;
		});

		// Corporate Event Detail
		builder.addCase(getCorporateEventDetailThunk.pending, (state) => {
			if (!state.corporateEventDetail) state.corporateEventDetailLoading = true;
			state.corporateEventDetailApiError = null;
		});
		builder.addCase(getCorporateEventDetailThunk.fulfilled, (state, action) => {
			state.corporateEventDetail = action.payload;
			state.corporateEventDetailLoading = false;
		});
		builder.addCase(getCorporateEventDetailThunk.rejected, (state, action) => {
			state.corporateEventDetailApiError = action.payload ?? DEFAULT_API_ERROR;
			state.corporateEventDetailLoading = false;
		});

		builder.addCase(getCorporateEventBidsThunk.pending, (state) => {
			state.corporateEventBidsLoading = true;
			state.corporateEventBidsApiError = null;
		});
		builder.addCase(getCorporateEventBidsThunk.fulfilled, (state, action) => {
			state.corporateEventBids = action.payload;
			state.corporateEventBidsLoading = false;
		});
		builder.addCase(getCorporateEventBidsThunk.rejected, (state, action) => {
			state.corporateEventBidsApiError = action.payload ?? DEFAULT_API_ERROR;
			state.corporateEventBidsLoading = false;
		});

		builder.addCase(declineCorporateEventThunk.pending, (state) => {
			state.corporateEventsApiError = null;
		});
		builder.addCase(declineCorporateEventThunk.rejected, (state, action) => {
			state.corporateEventsApiError = action.payload ?? DEFAULT_API_ERROR;
		});
	},
});

export const { resetCorporateEventsApiError, setCorporateEventDetail, resetCorporateEventDetailApiError } =
	corporateEventSlice.actions;
export default corporateEventSlice.reducer;
